<script lang="ts">
  import type { Grade, Run } from '../../shared/contract'
  import Sign from './Sign.svelte'
  import { GRADES, GRADE_NAME, STATUSES, dockCounts, type Filter, type Status } from './tally'

  /**
   * The filter, under the thumb: status (one at a time, with an explicit All)
   * over the four grades (any number; none means all). Each status carries its
   * count for the chosen grades, each grade its count for the chosen status.
   */
  let {
    runs,
    filter,
    desk = false,
    onstatus,
    ongrade,
  }: {
    runs: readonly Run[]
    filter: Filter
    desk?: boolean
    onstatus: (status: Status | null) => void
    ongrade: (grade: Grade) => void
  } = $props()

  const counts = $derived(dockCounts(runs, filter))
  const label = (s: Status | null) => s ?? 'All'
  /** A status nothing on the mountain has (open or groomed before the season) can't be chosen. */
  const none = $derived({
    open: !runs.some((r) => r.open),
    groomed: !runs.some((r) => r.groomed),
  })
</script>

<nav class="dock" class:desk aria-label="Filter runs">
  <div class="seg state" role="group" aria-label="Run state">
    {#each [null, ...STATUSES] as s (s ?? 'all')}
      {@const n = counts.status(s)}
      <button
        aria-pressed={filter.status === s}
        disabled={(s === 'open' && none.open) || (s === 'groomed' && none.groomed)}
        aria-label="{label(s)}: {n} runs"
        onclick={() => onstatus(s)}
      >
        <span class="n">{n}</span><span class="k">{label(s)}</span>
      </button>
    {/each}
  </div>
  <div class="seg grades" class:some={filter.grades.length > 0} role="group" aria-label="Grade">
    {#each GRADES as g (g)}
      {@const n = counts.grade(g)}
      <button
        aria-pressed={filter.grades.includes(g)}
        aria-label="{GRADE_NAME[g]}: {n} runs"
        onclick={() => ongrade(g)}
      >
        <Sign grade={g} /><span class="c">{n}</span>
      </button>
    {/each}
  </div>
</nav>

<style>
  .dock {
    flex: none;
    margin-top: var(--gap);
    border: 1px solid var(--glass-edge);
    background: var(--glass);
    border-radius: 18px;
    padding: 3px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .seg {
    display: flex;
    min-width: 0;
    gap: 2px;
  }
  button {
    flex: 1 1 0;
    min-width: 44px;
    min-height: 44px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ink-2);
    white-space: nowrap;
  }
  button[aria-pressed='true'] {
    background: var(--lit);
    color: var(--ink);
    box-shadow: inset 0 0 0 1px var(--glass-edge);
  }
  .state button {
    flex-direction: column;
    gap: 1px;
    height: 50px;
  }
  .n {
    font-size: 19px;
    line-height: 22px;
    font-weight: 300;
    color: var(--ink);
  }
  .k {
    font: 400 11px/14px var(--mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .state button[aria-pressed='true'] .k {
    color: var(--ink);
  }
  .state button:disabled {
    opacity: 0.4;
  }
  .grades {
    border-top: 1px solid var(--line);
    padding-top: 2px;
  }
  .grades button {
    gap: 8px;
    height: 44px;
    font-size: 14px;
  }
  .c {
    color: var(--ink-2);
  }
  /* Grades not chosen recede by their signs; their counts stay readable. */
  .grades.some button[aria-pressed='false'] :global(.g) {
    opacity: 0.45;
  }
  .grades.some button[aria-pressed='false'] .c {
    color: var(--ink-3);
  }
  @media (max-width: 999px) and (max-height: 760px) {
    .state button {
      height: 46px;
    }
    .n {
      font-size: 17px;
      line-height: 20px;
    }
  }
  @media (max-width: 400px) {
    .k {
      letter-spacing: 0.04em;
    }
  }

  /* Desktop: one row in the board's header. */
  .desk {
    margin: 0 0 0 auto;
    flex-direction: row;
    align-items: stretch;
    gap: 3px;
    border-radius: 16px;
  }
  .desk .state button {
    min-width: 74px;
    height: 48px;
  }
  .desk .grades {
    border-top: 0;
    padding-top: 0;
    border-left: 1px solid var(--line);
    padding-left: 3px;
  }
  .desk .grades button {
    height: 48px;
    min-width: 58px;
    padding: 0 8px;
  }
  /* A narrow board: the filter takes its own row, status over grades as on a phone. */
  @container board (max-width: 640px) {
    .desk {
      flex: 1 1 100%;
      margin: 0;
      flex-direction: column;
      gap: 2px;
    }
    .desk .state button {
      min-width: 44px;
    }
    .desk .grades {
      border-left: 0;
      padding-left: 0;
      border-top: 1px solid var(--line);
      padding-top: 2px;
    }
    .desk .grades button {
      min-width: 44px;
      height: 44px;
    }
  }
</style>
